"use client";

import { Header } from "@/components/shared/public";
import ScrollReveal from "@/components/shared/public/ScrollReveal";
import { useTranslations } from "next-intl";
import Image from "next/image";
import {
  grundrissschmiede,
  hamburgKinder,
  immomates,
  kaufpreis,
  nordhaus,
  nutzungsdauer,
} from "../../../../../public/images/partner";

export default function PartnerContent() {
  const t = useTranslations("partner");

  return (
    <div className="[--h1-weight:500] [--heading-scale:0.6]">
      <Header
        title={t("head.title")}
        subtitle={t("head.description")}
        containerClass="gs--page-column gs--page-column--narrow"
      />

      <div className="gs--page-column gs--page-column--narrow flex flex-col py-12 sm:py-16">
        {/* ── Section 1: Intro with floated Grundriss Schmiede logo ── */}
        <section className="gs--partner-section">
          <div>
            <Image
              src={grundrissschmiede}
              alt={t("sections.section1.imageAlt")}
              className="gs--partner-section__logo--inline float-left mr-4 mb-2"
            />
            <p className="gs--partner-section__text text-ring">
              {t.rich("sections.section1.text", {
                br: () => <br></br>,
                bold: (children) => <strong>{children}</strong>,
              })}
            </p>
          </div>
        </section>

        {/* ── Section 2: ImmoMates ── */}
        <ScrollReveal>
          <section className="gs--partner-section">
            <a
              className="gs--partner-section__logo"
              href="https://www.immomates.de/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                src={immomates}
                alt={t("sections.section2.imageAlt")}
                width={320}
                height={34}
                className="gs--partner-section__logo-img"
              />
            </a>
            <p className="gs--partner-section__text text-ring">
              {t.rich("sections.section2.text", {
                br: () => <br></br>,
                bold: (children) => <strong>{children}</strong>,
                link1: (children) => (
                  <a
                    href="https://www.immomates.de/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary font-bold transition-all"
                  >
                    {children}
                  </a>
                ),
              })}
            </p>
          </section>
        </ScrollReveal>

        {/* ── Section 3: Nordhaus ── */}
        <ScrollReveal>
          <section className="gs--partner-section">
            <a
              className="gs--partner-section__logo"
              href="https://nordhausimmobilien.de/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                src={nordhaus}
                alt={t("sections.section3.imageAlt")}
                width={280}
                height={81}
                className="gs--partner-section__logo-img"
              />
            </a>
            <p className="gs--partner-section__text text-ring">
              {t.rich("sections.section3.text", {
                br: () => <br></br>,
                bold: (children) => <strong>{children}</strong>,
                link1: (children) => (
                  <a
                    href="https://nordhausimmobilien.de/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary font-bold transition-all"
                  >
                    {children}
                  </a>
                ),
              })}
            </p>
          </section>
        </ScrollReveal>

        {/* ── Section 4: Kaufpreis-Aufteilung ── */}
        <ScrollReveal>
          <section className="gs--partner-section">
            <a
              className="gs--partner-section__logo"
              href="https://kaufpreis-aufteilung.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                src={kaufpreis}
                alt={t("sections.section5.imageAlt")}
                width={320}
                height={34}
                className="gs--partner-section__logo-img"
              />
            </a>
            <p className="gs--partner-section__text text-ring">
              {t.rich("sections.section5.text", {
                br: () => <br></br>,
                bold: (children) => <strong>{children}</strong>,
                link1: (children) => (
                  <a
                    href="https://kaufpreis-aufteilung.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary font-bold transition-all"
                  >
                    {children}
                  </a>
                ),
              })}
            </p>
          </section>
        </ScrollReveal>

        {/* ── Section 5: Nutzungsdauer ── */}
        <ScrollReveal>
          <section className="gs--partner-section">
            <a
              className="gs--partner-section__logo"
              href="https://www.nutzungsdauer.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                src={nutzungsdauer}
                alt={t("sections.section4.imageAlt")}
                width={320}
                height={40}
                className="gs--partner-section__logo-img"
              />
            </a>
            <p className="gs--partner-section__text text-ring">
              {t.rich("sections.section4.text", {
                br: () => <br></br>,
                bold: (children) => <strong>{children}</strong>,
                link1: (children) => (
                  <a
                    href="https://www.nutzungsdauer.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary font-bold transition-all"
                  >
                    {children}
                  </a>
                ),
              })}
            </p>
          </section>
        </ScrollReveal>

        {/* ── Section 6: Hamburg Macht Kinder Gesund e.V. ── */}
        <ScrollReveal>
          <section className="gs--partner-section gs--partner-section--last">
            <a
              className="gs--partner-section__logo gs--partner-section__logo--opaque"
              href="https://hhmkg.de/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                src={hamburgKinder}
                alt={t("sections.section6.imageAlt")}
                width={609}
                height={236}
                className="gs--partner-section__logo-img"
              />
            </a>
            <p className="gs--partner-section__text text-ring">
              {t.rich("sections.section6.text", {
                br: () => <br></br>,
                bold: (children) => <strong>{children}</strong>,
                link1: (children) => (
                  <a
                    href="https://hhmkg.de/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary font-bold transition-all"
                  >
                    {children}
                  </a>
                ),
              })}
            </p>
          </section>
        </ScrollReveal>
      </div>
    </div>
  );
}
